import React, { useEffect, useState } from "react";
import { Quote, Star } from "lucide-react";
import { api } from "../lib/api";
import Reveal from "./Reveal";

/**
 * TestimonialsStrip – shows 1-2 customer testimonials.
 * Loads from /api/testimonials (public) with sensible fallback so the layout never breaks.
 * Props:
 *   - max: number of testimonials to show (default 2)
 *   - variant: "light" | "dark" (background)
 *   - eyebrow: small badge text
 *   - heading: h2 text (React node ok)
 */
const FALLBACK = [
  {
    id: "fb1",
    quote:
      "Keanu und sein Team haben unseren Auftritt komplett gedreht. Innerhalb weniger Wochen deutlich mehr qualifizierte Anfragen – ohne Marketing-Blabla.",
    author: "Marco B.",
    role: "Inhaber",
    company: "Balkan Grill",
    rating: 5,
  },
  {
    id: "fb2",
    quote:
      "Endlich Content, der nicht nur schön aussieht, sondern auch Umsatz bringt. Absolute Empfehlung.",
    author: "Sarah K.",
    role: "Marketing Managerin",
    company: "Prime Dance & Nightlife",
    rating: 5,
  },
];

const Stars = ({ rating = 5 }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        size={14}
        className={
          i < rating
            ? "fill-amber-400 text-amber-400"
            : "fill-neutral-300 text-neutral-300"
        }
      />
    ))}
  </div>
);

const Card = ({ t, dark }) => (
  <div
    className={`relative p-8 md:p-10 rounded-3xl border transition-all duration-500 hover:-translate-y-1 ${
      dark
        ? "bg-white/[0.04] border-white/10 hover:border-white/25 hover:shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)]"
        : "bg-white border-black/5 hover:border-black/15 hover:shadow-[0_30px_60px_-20px_rgba(0,0,0,0.15)]"
    }`}
    data-testid="testimonial-card"
  >
    <div
      className={`absolute top-6 right-6 w-12 h-12 rounded-2xl flex items-center justify-center ${
        dark ? "bg-white/5" : "bg-neutral-100"
      }`}
    >
      <Quote size={18} className="text-pink-500" />
    </div>
    <Stars rating={t.rating || 5} />
    <p
      className={`mt-5 text-lg md:text-xl leading-relaxed ${
        dark ? "text-neutral-100" : "text-neutral-800"
      }`}
    >
      „{t.quote}&rdquo;
    </p>
    <div className="mt-8 flex items-center gap-3">
      {t.avatar ? (
        <img
          src={t.avatar}
          alt={t.author}
          className="w-11 h-11 rounded-full object-cover border border-white/20"
          loading="lazy"
        />
      ) : (
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-pink-500 via-orange-500 to-purple-500 text-white flex items-center justify-center font-bold text-sm">
          {(t.author || "?").slice(0, 1)}
        </div>
      )}
      <div>
        <div
          className={`font-semibold text-sm ${
            dark ? "text-white" : "text-neutral-950"
          }`}
        >
          {t.author}
        </div>
        <div className={`text-xs ${dark ? "text-neutral-400" : "text-neutral-500"}`}>
          {t.role}
          {t.role && t.company ? " · " : ""}
          {t.company}
        </div>
      </div>
      {t.logo && (
        <img
          src={t.logo}
          alt={t.company}
          className="ml-auto h-6 md:h-8 opacity-70 object-contain max-w-[80px]"
          loading="lazy"
        />
      )}
    </div>
  </div>
);

const TestimonialsStrip = ({
  max = 2,
  variant = "light",
  eyebrow = "Kundenstimmen",
  heading = (
    <>
      Was unsere Kunden{" "}
      <span className="bg-gradient-to-r from-pink-500 via-orange-500 to-purple-500 bg-clip-text text-transparent">
        wirklich sagen.
      </span>
    </>
  ),
}) => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api
      .get("/testimonials")
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, []);

  const list = (items && items.length > 0 ? items : FALLBACK).slice(0, max);
  const dark = variant === "dark";

  return (
    <section
      className={`py-20 md:py-28 relative overflow-hidden ${
        dark ? "bg-[#0a0a0a] text-white" : "bg-neutral-50"
      }`}
      data-testid="testimonials-section"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="max-w-2xl mb-12">
            <span
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] font-semibold ${
                dark
                  ? "bg-white/5 border border-white/10 text-neutral-300"
                  : "bg-white border border-black/10 text-neutral-700"
              }`}
            >
              <Star size={12} className="fill-amber-400 text-amber-400" />
              {eyebrow}
            </span>
            <h2
              className={`font-display font-extrabold text-3xl md:text-5xl lg:text-6xl tracking-[-0.03em] leading-[1.05] mt-6 ${
                dark ? "text-white" : "text-neutral-950"
              }`}
            >
              {heading}
            </h2>
          </div>
        </Reveal>

        <div
          className={`grid gap-6 ${
            list.length === 1 ? "md:grid-cols-1 max-w-3xl" : "md:grid-cols-2"
          }`}
        >
          {list.map((t, i) => (
            <Reveal key={t.id} delay={i * 100}>
              <Card t={t} dark={dark} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsStrip;
